import { useEffect, useState } from "react";
import { createClient, ContentfulClientApi, CreateClientParams, EntryCollection, EntrySkeletonType, EntriesQueries } from "contentful";

const contentfulConfigDomId = 'contentful-config';

// Query key can be used to re-fetch after initialization, by providing a unique value for each use.
// For example, passing JSON.stringify(parameters) will trigger a new fetch whenever a parameter changes.
const useContentful = (parameters: EntriesQueries<EntrySkeletonType, undefined>, queryKey = '') => {
  const [client, setClient] = useState<ContentfulClientApi<undefined>>();
  const [locale, setLocale] = useState('en-US');
  const [isLoadingContentful, setIsLoadingContentful] = useState(true);
  const [contentfulData, setContentfulData] = useState<EntryCollection<EntrySkeletonType, "WITHOUT_UNRESOLVABLE_LINKS", string>>();
  const [contentfulError, setContentfulError] = useState<string | null>(null);

  if (!client) {
    const configEl = document.getElementById(contentfulConfigDomId);

    const language = configEl?.dataset.language;
    const market = configEl?.dataset.market;
    // "en-US" should be used in place of "en" due to Contentful configuration,
    // so this should only be updated if another language is detected, or in
    // the specific case of en-GB for the uk market.
    if (language && language != 'en') {
      setLocale(language);
    } else if (market == 'gb' && language == 'en') {
      setLocale('en-GB');
    }

    const space = configEl?.dataset?.spaceid;
    const accessToken = configEl?.dataset?.token;
    const host = configEl?.dataset?.hostname;
    const environment = configEl?.dataset?.environment;

    if (space && accessToken) {
      const clientConfig: CreateClientParams = { space, accessToken };
      if (host) clientConfig.host = host;
      if (environment) clientConfig.environment = environment;

      const contentfulClient = createClient(clientConfig);
      setClient(contentfulClient);
    }
  }

  useEffect(() => {
    const getEntries = async (parameters: EntriesQueries<EntrySkeletonType, undefined>) => {
      try {
        const requestParameters = { ...parameters, locale };
        const data = await client?.withoutUnresolvableLinks.getEntries(requestParameters);
        setContentfulData(data);
      } catch (error) {
        setContentfulError(error as string);
      } finally {
        setIsLoadingContentful(false);
      }
    }
    
    getEntries(parameters);
  }, [queryKey]);

  return { contentfulData, contentfulError, isLoadingContentful };
}

export default useContentful;